import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import type { AgGridReact } from 'ag-grid-react';
import type { BulkManageRow } from '../types';
import type {
  ColDef,
  ColGroupDef,
  GetDataPath,
  GetRowIdFunc,
  GridColumnsChangedEvent,
  GridReadyEvent,
  IRowNode,
  ModelUpdatedEvent,
  RowClassParams,
  RowClassRules,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { isBulkManageCashRow, isBulkManageFundRow, isBulkManageOptionRow, isSecondaryInvestment } from '../utils';
import styled, { ThemeContext, css } from 'styled-components';
import { isNil, sumBy } from 'lodash';
import { columnStyles } from '../columns';
import { GetColor } from 'venn-ui-kit';
import { GroupCellClass, NameCellClass, SecondaryCellClass, TotalCellClass } from '../constants';
import DataGrid from '../../data-grid/DataGrid';

const rowSelection: RowSelectionOptions<BulkManageRow> = {
  mode: 'multiRow',
  groupSelects: 'descendants',
  headerCheckbox: false,
  checkboxes: false,
  hideDisabledCheckboxes: false,
  isRowSelectable: ({ data }) => {
    if (isBulkManageCashRow(data) || isBulkManageOptionRow(data)) {
      return false;
    }
    return !isBulkManageFundRow(data) || (data.investment?.assetType !== 'BENCHMARK' && !data.secondary);
  },
};

const defaultColDef: ColDef<BulkManageRow> = {
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  editable: false,
  resizable: false,
};

const defaultIcons = {
  groupExpanded: '<i class="fa fa-caret-down"/>',
  groupContracted: '<i class="fa fa-caret-right"/>',
  check: '<i class="fa fa-caret-up"/>',
};

const getDataPath: GetDataPath<BulkManageRow> = (data) => data.fullPath;
const getRowId: GetRowIdFunc<BulkManageRow> = ({ data }) => data.rowId;

const ROW_HEIGHT = 37;
const HEADER_HEIGHT = 25;
const BORDER_BUFFER = 2;
const VIEWPORT_OFFSET = 280;
const SELECTION_FOOTER_HEIGHT = 70;

interface BulkDataGridProps {
  gridRef: React.MutableRefObject<AgGridReact | null>;
  rowData: BulkManageRow[];
  pinnedRowData: BulkManageRow[];
  columnDefs: (ColDef | ColGroupDef)[];
  onSelectionChanged: (event: SelectionChangedEvent) => void;
  groupHeaderHeight?: number;
  isExternalFilterPresent?: () => boolean;
  doesExternalFilterPass?: (node: IRowNode<BulkManageRow>) => boolean;
  disableSelection?: boolean;
  autoGroupColumnDef: ColDef<BulkManageRow, BulkManageRow['name']>;
  rowClassRules: RowClassRules<BulkManageRow>;
  hasSelections?: boolean;
  isAllocationsTable?: boolean;
}
export const BulkManagementTablePrimitivesGrid = React.memo(
  ({
    gridRef,
    rowData,
    columnDefs,
    onSelectionChanged,
    pinnedRowData,
    groupHeaderHeight,
    isExternalFilterPresent,
    doesExternalFilterPass,
    disableSelection,
    rowClassRules,
    autoGroupColumnDef,
    hasSelections,
    isAllocationsTable,
  }: BulkDataGridProps) => {
    const { Colors } = useContext(ThemeContext);

    const defaultRowStyle = useMemo(() => {
      const secondaryRowStyle = { background: Colors.GreyScale.Grey10 };
      return ({ node }: RowClassParams<BulkManageRow, unknown>) =>
        isSecondaryInvestment(node.data) ? secondaryRowStyle : undefined;
    }, [Colors.GreyScale.Grey10]);

    const [apiMinWidth, setApiMinWidth] = useState(0);
    const [gridHeight, setGridHeight] = useState<number | undefined>(undefined);

    const onGridColumnsChanged = (params: GridColumnsChangedEvent) => {
      setApiMinWidth(
        resolveColumnMinWidth(autoGroupColumnDef) +
          sumBy(params.api.getColumns(), (col) => resolveColumnMinWidth(col.getColDef())),
      );
    };

    const updateGridHeight = useCallback(
      (api: GridReadyEvent['api']) => {
        const displayedRowCount = api.getDisplayedRowCount();
        const pinnedRowCount = pinnedRowData?.length ?? 0;
        const totalRows = displayedRowCount + pinnedRowCount;
        const extraHeightForBorderedTable = isAllocationsTable ? (groupHeaderHeight ?? 0) : 0;
        const contentHeight = totalRows * ROW_HEIGHT + HEADER_HEIGHT + BORDER_BUFFER + extraHeightForBorderedTable;
        const footerOffset = hasSelections ? SELECTION_FOOTER_HEIGHT : 0;
        const maxHeight = window.innerHeight - VIEWPORT_OFFSET - footerOffset;
        setGridHeight(Math.min(contentHeight, maxHeight));
      },
      [groupHeaderHeight, hasSelections, pinnedRowData?.length, isAllocationsTable],
    );

    const onGridReady = useCallback(
      (event: GridReadyEvent) => {
        updateGridHeight(event.api);
      },
      [updateGridHeight],
    );

    const onModelUpdated = useCallback(
      (event: ModelUpdatedEvent) => {
        updateGridHeight(event.api);
      },
      [updateGridHeight],
    );

    useEffect(() => {
      if (gridRef.current?.api) {
        updateGridHeight(gridRef.current.api);
      }
    }, [hasSelections, updateGridHeight, gridRef]);

    useEffect(() => {
      const handleResize = () => {
        if (gridRef.current?.api) {
          updateGridHeight(gridRef.current.api);
        }
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [updateGridHeight, gridRef]);

    return (
      <GridContainer isAllocationsTable={isAllocationsTable} style={gridHeight ? { height: gridHeight } : undefined}>
        <StyledDataGrid
          minWidth={apiMinWidth}
          isAllocationsTable={isAllocationsTable}
          onGridColumnsChanged={onGridColumnsChanged}
          onGridReady={onGridReady}
          onModelUpdated={onModelUpdated}
          className="ag-theme-alpine"
          gridRef={gridRef}
          getRowId={getRowId}
          rowSelection={!disableSelection ? rowSelection : undefined}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowData}
          getRowStyle={defaultRowStyle}
          domLayout="normal"
          icons={defaultIcons}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          onSelectionChanged={onSelectionChanged}
          suppressContextMenu
          treeData
          autoGroupColumnDef={autoGroupColumnDef}
          getDataPath={getDataPath}
          groupDefaultExpanded={-1} // -1 expands all groups by default
          suppressGroupRowsSticky
          rowClassRules={rowClassRules}
          pinnedBottomRowData={pinnedRowData}
          groupHeaderHeight={groupHeaderHeight}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </GridContainer>
    );
  },
);
BulkManagementTablePrimitivesGrid.displayName = 'BulkManagementTablePrimitivesGrid';

function resolveColumnMinWidth({
  flex,
  width,
  minWidth,
  maxWidth,
}: {
  flex?: number;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
}): number {
  if (isNil(minWidth ?? width ?? maxWidth ?? flex)) {
    return 200;
  }
  minWidth ??= 0;
  maxWidth ??= Infinity;

  if (maxWidth < minWidth) {
    return maxWidth;
  }
  if (flex) {
    return minWidth;
  }
  return Math.max(width ?? 0, minWidth);
}

const StyledDataGrid = styled(DataGrid<BulkManageRow>)<{
  minWidth: number;
  isAllocationsTable?: boolean;
}>`
  width: max(${(props) => props.minWidth}px, 100%);
  //width: ${(props) => (props.isAllocationsTable ? '100%' : `max(${props.minWidth}px, 100%)`)};

  ${(props) =>
    props.isAllocationsTable
      ? css`
          .ag-row .ag-cell:first-child,
          .ag-header-row .ag-header-cell:first-child {
            padding-left: 16px;
            border-left: 1px solid ${GetColor.GreyScale.Grey30};
          }

          .ag-row .ag-cell:last-child,
          .ag-header-row .ag-header-cell:last-child {
            padding-right: 16px;
          }
        `
      : css``}

  .ag-header-group-cell,
  .ag-header-cell-comp-wrapper {
    height: 100%;
    padding: 0 !important;
  }
`;

const GridContainer = styled.div<{ isAllocationsTable?: boolean }>`
  width: 100%;
  height: calc(100vh - ${VIEWPORT_OFFSET}px);
  max-height: calc(100vh - ${VIEWPORT_OFFSET}px);

  .ag-header-cell {
    ${(props) =>
      props.isAllocationsTable
        ? css`
            border-top: 1px solid ${GetColor.GreyScale.Grey30} !important;
          `
        : css`
            border-top: none !important;
          `}
  }

  .ag-root-wrapper {
    font-family: ${(props) => props.theme.Typography.fontFamily};
    height: 100%;

    --ag-cell-horizontal-border: solid ${GetColor.GreyScale.Grey40};
    --ag-header-background-color: ${GetColor.White};
    --ag-background-color: ${GetColor.White};
    --ag-odd-row-background-color: ${GetColor.White};

    --ag-row-group-indent-size: 20px;
    --ag-cell-widget-spacing: 4px;
    --ag-cell-horizontal-padding: 4px;
  }

  ${columnStyles}
  .${NameCellClass} {
    border-right: 1px solid ${GetColor.GreyScale.Grey30};
  }

  .${GroupCellClass} {
    font-weight: bold;
    color: ${GetColor.Black};
  }

  .${SecondaryCellClass} {
    font-style: italic;
    color: ${GetColor.GreyScale.Grey80};
  }

  .${TotalCellClass} {
    font-style: normal;
    font-weight: bold;
    color: ${GetColor.Black};
  }

  .ag-checkbox-input-wrapper.ag-checked,
  .ag-checkbox-input-wrapper.ag-indeterminate {
    // Sensibly, the background-color is the color of the checkmark, and color is the color of the background.
    --ag-checkbox-background-color: ${GetColor.White};
    --ag-checkbox-checked-color: ${GetColor.DarkBlue};
    --ag-checkbox-indeterminate-color: ${GetColor.DarkBlue};
  }

  .ag-header {
    border-bottom: none;
    font-size: 12px;
  }

  .ag-header-container {
    border-bottom: 1px solid ${GetColor.Black};
    font-size: 12px;
  }

  .ag-ltr .ag-row-group-leaf-indent {
    margin-left: 0;
  }

  .ag-floating-bottom,
  .ag-floating-bottom-viewport {
    // Allow overflow for the axis
    overflow: visible !important;
    border: none;
  }

  .ag-row-pinned:last-child {
    border-top: 1px solid ${GetColor.Black};
  }
`;
